import type { Period } from "../hooks/usePeriod";

export type SerieOptions =
  | Highcharts.SeriesLineOptions
  | Highcharts.SeriesFlagsOptions
  | Highcharts.SeriesArearangeOptions;

const pointBetween = (
  data: Highcharts.PointOptionsObject,
  min: number | null,
  max: number | null,
) =>
  (min === null || (data.x !== undefined && data.x >= min)) &&
  (max === null || (data.x !== undefined && data.x <= max));

function isInLegend<T extends Highcharts.SeriesOptionsType>(serie: T, period: Period | undefined) {
  if (!("data" in serie)) return false;
  const serieData = serie.data as Highcharts.PointOptionsObject[] | undefined;
  if (!serieData || serieData.length < 1 || !serie.showInLegend) return false;
  if (period === undefined) return true;

  const min = period.from;
  const max = period.to;
  const valueProperty = serie.type === "line" ? "y" : "high";
  const dataInPeriod = serieData.filter(d => pointBetween(d, min, max));

  if (dataInPeriod.some(d => d[valueProperty] !== null)) {
    return true;
  }

  // Code below is to find whether the serie is visible in the chart even every point is not
  // see PR #496
  const serieDataReversed = [...serieData].reverse();
  const reverseIndex = serieDataReversed.findIndex(d => d.x !== undefined && min && d.x < min);
  const lastAnteriorIndex = reverseIndex < 0 ? -1 : serieData.length - 1 - reverseIndex;
  const firstPosteriorIndex = serieData.findIndex(d => d.x !== undefined && max && d.x > max);

  return (
    (lastAnteriorIndex > 0 &&
      lastAnteriorIndex < serieData.length - 1 &&
      serieData[lastAnteriorIndex]?.[valueProperty] &&
      serieData[lastAnteriorIndex + 1]?.[valueProperty] !== null) ||
    (firstPosteriorIndex > 0 &&
      firstPosteriorIndex > 0 &&
      serieData[firstPosteriorIndex]?.[valueProperty] &&
      serieData[firstPosteriorIndex - 1]?.[valueProperty] !== null)
  );
}

export default isInLegend;
